<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  selectedPlayers: {
    type: Object,
    default: () => ({})
  },
  initialActivePlayerId: {
    type: String,
    default: null
  },
  initialTab: {
    type: String,
    default: 'boxscores'
  }
})

const emit = defineEmits(['update:modelValue', 'players-updated', 'close'])

const comparisonLoading = ref(false)
const comparisonError = ref(null)
const comparisonResult = ref(null)
const activeComparePlayerId = ref(props.initialActivePlayerId || null)
const selectedGamesByPlayer = ref({})
const overlaySearch = ref('')
const overlaySearchResults = ref([])
const overlaySearchLoading = ref(false)
let overlaySearchDebounce = null
const playerStats = ref({})
const rosterHoverPlayer = ref(null)
const playerResources = ref({})
const resourcesLoading = ref(false)
const resourcesError = ref(null)
const activePlayerTab = ref(props.initialTab)
const selectedContextMenu = ref({ visible: false, x: 0, y: 0, player: null })
const deletePlayerDialog = ref({ open: false, player: null })
const deletePlayerLoading = ref(false)
const deletePlayerError = ref(null)
const tableContextMenu = ref({ visible: false, x: 0, y: 0 })
const moveDialog = ref({ open: false, targetPlayerId: '' })
const moveLoading = ref(false)
const moveError = ref(null)
const moveSuccess = ref(null)
const deleteDialog = ref({ open: false })
const deleteLoading = ref(false)
const deleteError = ref(null)
const deleteGameByIdDialog = ref({ open: false })
const deleteGameByIdLoading = ref(false)
const deleteGameByIdError = ref(null)
const fullGameDialog = ref({ open: false })
const fullGameLoading = ref(false)
const fullGameError = ref(null)
const fullGameBoxscores = ref([])
const fullGameSelectedIds = ref(new Set())
const fullGameDeleteLoading = ref(false)
const fullGameDeleteError = ref(null)
const leagueGamesDialog = ref({ open: false })
const leagueGamesLoading = ref(false)
const leagueGamesError = ref(null)
const leagueGamesData = ref([])
const leagueGamesLeagueName = ref('')
const leagueGamesSeason = ref('')
const leagueGamesFilterType = ref('league') // 'league' | 'team' | 'opponent'
const leagueGamesFilterLabel = ref('')
const leagueGamesPerPage = ref(200)
const updateLeagueNameDialog = ref({ open: false })
const updateLeagueNameNewName = ref('')
const updateLeagueNameLoading = ref(false)
const updateLeagueNameError = ref(null)
const updateLeagueNameRowCount = ref(null)
const updateLeagueNameCountLoading = ref(false)

const toIdString = (val) => val !== undefined && val !== null ? String(val) : ''

const valueOrId = (p) => toIdString(p?.player_id || p?.id)

const selectedPlayerIds = computed(() => Object.keys(props.selectedPlayers))
const selectedPlayersList = computed(() => Object.values(props.selectedPlayers))

const comparisonGamesByPlayer = computed(() => {
  const result = {}
  const games = comparisonResult.value?.data || []
  games.forEach(game => {
    const pid = toIdString(game.player_id || game.playerId)
    if (!pid) return
    if (!result[pid]) result[pid] = []
    result[pid].push(game)
  })
  return result
})

const gamesForActivePlayer = computed(() => {
  if (!activeComparePlayerId.value) return []
  return comparisonGamesByPlayer.value[activeComparePlayerId.value] || []
})

const selectedGameIdsForActive = computed(() => {
  if (!activeComparePlayerId.value) return new Set()
  return new Set(selectedGamesByPlayer.value[activeComparePlayerId.value] || [])
})

const isAllGamesSelectedForActive = computed(() => {
  const games = gamesForActivePlayer.value
  if (!games.length) return false
  return games.every(g => selectedGameIdsForActive.value.has(String(g.id)))
})

const moveCandidatePlayers = computed(() => {
  return selectedPlayersList.value.filter(p => p.id !== activeComparePlayerId.value)
})

const getPlayerStat = (playerId, statKey) => {
  const pid = toIdString(playerId)
  if (!pid) return 0
  const stats = playerStats.value[pid] || {}
  const resources = playerResources.value[pid] || {}
  
  // Return stat from playerStats if available, otherwise use resources as fallback
  switch (statKey) {
    case 'projections':
      return stats.projections ?? resources.projections?.length ?? 0
    case 'balanced_lines':
      return stats.balanced_lines ?? resources.balanced_lines?.length ?? 0
    case 'boxscores':
      return stats.boxscores ?? (comparisonGamesByPlayer.value[pid] || []).length ?? 0
    case 'rosters_count':
      return stats.rosters_count ?? resources.rosters?.length ?? 0
    case 'aliases':
      return stats.aliases ?? resources.aliases?.length ?? 0
    default:
      return 0
  }
}

const tabCount = (type) => {
  const pid = toIdString(activeComparePlayerId.value)
  if (!pid) return 0
  const stats = playerStats.value[pid] || {}
  const resources = playerResources.value[pid] || {}
  const fallback = {
    boxscores: (comparisonGamesByPlayer.value[pid] || []).length,
    rosters: resources.rosters?.length || 0,
    projections: resources.projections?.length || 0,
    balanced: resources.balanced_lines?.length || 0,
    aliases: resources.aliases?.length || 0
  }
  switch (type) {
    case 'boxscores':
      return stats.boxscores ?? fallback.boxscores
    case 'rosters':
      return stats.rosters_count ?? fallback.rosters
    case 'projections':
      return stats.projections ?? fallback.projections
    case 'balanced':
      return stats.balanced_lines ?? fallback.balanced
    case 'aliases':
      return stats.aliases ?? fallback.aliases
    default:
      return 0
  }
}

const toggleGameSelection = (game) => {
  if (!activeComparePlayerId.value) return;
  const pid = activeComparePlayerId.value;
  const current = new Set(selectedGamesByPlayer.value[pid] || []);
  const key = String(game.id);
  if (current.has(key)) {
    current.delete(key);
  } else {
    current.add(key);
  }
  selectedGamesByPlayer.value = {
    ...selectedGamesByPlayer.value,
    [pid]: Array.from(current)
  };
};

const toggleSelectAllForActive = () => {
  const pid = activeComparePlayerId.value;
  if (!pid) return;
  if (isAllGamesSelectedForActive.value) {
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [pid]: [] };
  } else {
    const allIds = gamesForActivePlayer.value.map(g => String(g.id));
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [pid]: allIds };
  }
};

const isGameSelected = (game) => {
  return selectedGameIdsForActive.value.has(String(game.id));
};

const setActiveComparePlayer = (playerId) => {
  activeComparePlayerId.value = toIdString(playerId);
  fetchComparisonData();
};

const closeOverlay = () => {
  emit('update:modelValue', false)
  emit('close')
}

const clearSelection = () => {
  emit('players-updated', {})
  selectedGamesByPlayer.value = {};
};

const removeFromSelectedOnly = (player) => {
  const pid = toIdString(player.id);
  const updated = { ...props.selectedPlayers };
  delete updated[pid];
  emit('players-updated', updated);
  if (activeComparePlayerId.value === pid) {
    const remaining = Object.keys(updated);
    activeComparePlayerId.value = remaining[0] || null;
    if (!remaining.length) {
      closeOverlay();
      comparisonResult.value = null;
      selectedGamesByPlayer.value = {};
      return;
    }
  }
};

const onPlayerNameBlur = async (player, event) => {
  const newName = (event?.target?.innerText || '').trim();
  if (!newName || newName === player.name) {
    event.target.innerText = player.name;
    return;
  }

  const updatePlayers = window.confirm('Update player name in players table?');
  const updateBoxscores = window.confirm('Also update player name in boxscores?');
  if (!updatePlayers && !updateBoxscores) {
    event.target.innerText = player.name;
    return;
  }

  try {
    await axios.post('/api/compare-players/update-name', {
      player_id: player.id,
      name: newName,
      update_players: updatePlayers,
      update_boxscores: updateBoxscores
    });
    // Update local name
    const updated = { ...props.selectedPlayers };
    updated[player.id] = { ...updated[player.id], name: newName };
    emit('players-updated', updated);
  } catch (e) {
    console.error('Failed to update player name', e);
    event.target.innerText = player.name;
  }
};

const fetchPlayerResources = async (playerId) => {
  if (!playerId) return;
  resourcesLoading.value = true;
  resourcesError.value = null;
  try {
    const resp = await axios.get(`/api/compare-players/resources/${playerId}`);
    playerResources.value = {
      ...playerResources.value,
      [toIdString(playerId)]: resp.data
    };
  } catch (e) {
    resourcesError.value = 'Failed to load player resources';
    console.error('fetchPlayerResources error', e);
  } finally {
    resourcesLoading.value = false;
  }
};

const openSelectedContextMenu = (event, player) => {
  event.preventDefault();
  selectedContextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    player
  };
  document.addEventListener('click', closeSelectedContextMenu);
};

const closeSelectedContextMenu = () => {
  selectedContextMenu.value = { visible: false, x: 0, y: 0, player: null };
  document.removeEventListener('click', closeSelectedContextMenu);
};

const openDeletePlayerDialog = (player) => {
  deletePlayerDialog.value = { open: true, player };
  closeSelectedContextMenu();
  deletePlayerError.value = null;
};

const closeDeletePlayerDialog = () => {
  deletePlayerDialog.value = { open: false, player: null };
  deletePlayerError.value = null;
};

const deletePlayerFromTable = async () => {
  if (!deletePlayerDialog.value.player) {
    deletePlayerError.value = 'No player selected.';
    return;
  }
  
  deletePlayerLoading.value = true;
  deletePlayerError.value = null;
  
  try {
    const playerId = parseInt(deletePlayerDialog.value.player.id);
    
    await axios.post('/api/compare-players/delete-player-from-table', {
      player_id: playerId
    });
    
    // Remove from selected players
    const pid = toIdString(deletePlayerDialog.value.player.id);
    const updated = { ...props.selectedPlayers };
    delete updated[pid];
    emit('players-updated', updated);
    
    // If this was the active player, switch to another
    if (activeComparePlayerId.value === pid) {
      const remaining = Object.keys(updated);
      activeComparePlayerId.value = remaining[0] || null;
      if (!remaining.length) {
        closeOverlay();
        comparisonResult.value = null;
        selectedGamesByPlayer.value = {};
        closeDeletePlayerDialog();
        return;
      }
    }
    
    // Refresh comparison data
    await fetchComparisonData();
    
    // Close dialog
    closeDeletePlayerDialog();
  } catch (error) {
    deletePlayerError.value = 'Failed to delete player: ' + (error.response?.data?.message || error.message);
    console.error('Error deleting player:', error);
  } finally {
    deletePlayerLoading.value = false;
  }
};

const openMoveDialog = () => {
  moveDialog.value = { open: true, targetPlayerId: '' };
  tableContextMenu.value.visible = false;
  moveError.value = null;
  moveSuccess.value = null;
};

const openDeleteDialog = () => {
  deleteDialog.value = { open: true };
  tableContextMenu.value.visible = false;
  deleteError.value = null;
};

const closeDeleteDialog = () => {
  deleteDialog.value = { open: false };
  deleteError.value = null;
};

const openDeleteGameByIdDialog = () => {
  deleteGameByIdDialog.value = { open: true };
  tableContextMenu.value.visible = false;
  deleteGameByIdError.value = null;
};

const closeDeleteGameByIdDialog = () => {
  deleteGameByIdDialog.value = { open: false };
  deleteGameByIdError.value = null;
};

const fullGameIsAllSelected = computed(() => {
  const list = fullGameBoxscores.value;
  if (!list.length) return false;
  return list.every(b => fullGameSelectedIds.value.has(String(b.id)));
});

const fullGameSelectedCount = computed(() => fullGameSelectedIds.value.size);

const toggleFullGameRow = (b) => {
  const id = String(b.id);
  const next = new Set(fullGameSelectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  fullGameSelectedIds.value = next;
};

const toggleFullGameSelectAll = () => {
  if (fullGameIsAllSelected.value) {
    fullGameSelectedIds.value = new Set();
  } else {
    fullGameSelectedIds.value = new Set(fullGameBoxscores.value.map(b => String(b.id)));
  }
};

const isFullGameRowSelected = (b) => fullGameSelectedIds.value.has(String(b.id));

const deleteSelectedFullGameBoxscores = async () => {
  const ids = Array.from(fullGameSelectedIds.value).map(id => parseInt(id)).filter(n => !isNaN(n));
  if (!ids.length) return;
  fullGameDeleteLoading.value = true;
  fullGameDeleteError.value = null;
  try {
    await axios.post('/api/compare-players/delete-boxscores', { boxscore_ids: ids });
    fullGameBoxscores.value = fullGameBoxscores.value.filter(b => !fullGameSelectedIds.value.has(String(b.id)));
    fullGameSelectedIds.value = new Set();
    await fetchComparisonData();
  } catch (e) {
    fullGameDeleteError.value = 'Failed to delete: ' + (e.response?.data?.message || e.message);
  } finally {
    fullGameDeleteLoading.value = false;
  }
};

const openFullGameDialog = async () => {
  fullGameDialog.value = { open: true };
  tableContextMenu.value.visible = false;
  fullGameError.value = null;
  fullGameDeleteError.value = null;
  fullGameBoxscores.value = [];
  fullGameSelectedIds.value = new Set();
  fullGameLoading.value = true;
  try {
    const gameIds = uniqueGameIdsForDeletion.value.map(id => String(id));
    const resp = await axios.get('/api/compare-players/boxscores-by-game-id', {
      params: { game_ids: gameIds.join(',') }
    });
    fullGameBoxscores.value = resp.data?.boxscores || resp.data?.data || [];
  } catch (e) {
    fullGameError.value = 'Failed to load full game: ' + (e.response?.data?.message || e.message);
  } finally {
    fullGameLoading.value = false;
  }
};

const closeFullGameDialog = () => {
  fullGameDialog.value = { open: false };
  fullGameError.value = null;
  fullGameDeleteError.value = null;
  fullGameBoxscores.value = [];
  fullGameSelectedIds.value = new Set();
};

const fetchLeagueGames = async () => {
  const filterType = leagueGamesFilterType.value;
  const filterValue = leagueGamesLeagueName.value;
  if (!filterValue) return;
  leagueGamesLoading.value = true;
  leagueGamesError.value = null;
  try {
    const raw = parseInt(leagueGamesPerPage.value);
    const perPage = Math.min(10000, Math.max(1, isNaN(raw) ? 200 : raw));
    const params = { per_page: perPage };
    if (filterType === 'league') params.league_name = filterValue;
    else if (filterType === 'team') params.team_name = filterValue;
    else if (filterType === 'opponent') params.opponent_team_name = filterValue;
    if (leagueGamesSeason.value) params.season = leagueGamesSeason.value;
    const resp = await axios.get('/api/boxscores-management/comparable-data/filtered', { params });
    const data = resp.data?.data ?? resp.data ?? [];
    leagueGamesData.value = Array.isArray(data) ? data : [];
  } catch (e) {
    leagueGamesError.value = 'Failed to load games: ' + (e.response?.data?.message || e.message);
  } finally {
    leagueGamesLoading.value = false;
  }
};

const openFilteredGamesDialog = async (filterType, value, season = '') => {
  if (!value) return;
  leagueGamesDialog.value = { open: true };
  leagueGamesFilterType.value = filterType;
  leagueGamesLeagueName.value = value;
  leagueGamesSeason.value = season || '';
  leagueGamesFilterLabel.value = value;
  leagueGamesError.value = null;
  leagueGamesData.value = [];
  await fetchLeagueGames();
};

const openLeagueGamesDialog = async (leagueName, season = '') => {
  await openFilteredGamesDialog('league', leagueName, season);
};

const closeLeagueGamesDialog = () => {
  leagueGamesDialog.value = { open: false };
  leagueGamesError.value = null;
  leagueGamesData.value = [];
};

const openUpdateLeagueNameDialog = async () => {
  updateLeagueNameDialog.value = { open: true };
  updateLeagueNameNewName.value = leagueGamesLeagueName.value || leagueGamesFilterLabel.value || '';
  updateLeagueNameError.value = null;
  updateLeagueNameRowCount.value = null;
  const current = leagueGamesLeagueName.value || leagueGamesFilterLabel.value;
  if (current) {
    try {
      const resp = await axios.get('/api/boxscores-management/league-update-preview', {
        params: { current_league_name: current }
      });
      updateLeagueNameRowCount.value = resp.data?.count ?? null;
    } catch (e) {
      updateLeagueNameRowCount.value = null;
    }
  }
};

const closeUpdateLeagueNameDialog = () => {
  updateLeagueNameDialog.value = { open: false };
  updateLeagueNameNewName.value = '';
  updateLeagueNameError.value = null;
  updateLeagueNameRowCount.value = null;
};

const submitUpdateLeagueName = async () => {
  const current = leagueGamesLeagueName.value || leagueGamesFilterLabel.value;
  const newName = (updateLeagueNameNewName.value || '').trim();
  if (!current || !newName || current === newName) return;

  updateLeagueNameLoading.value = true;
  updateLeagueNameError.value = null;
  try {
    await axios.post('/api/boxscores-management/update-league-name', {
      current_league_name: current,
      new_league_name: newName
    });
    leagueGamesLeagueName.value = newName;
    leagueGamesFilterLabel.value = newName;
    await fetchLeagueGames();
    closeUpdateLeagueNameDialog();
  } catch (e) {
    updateLeagueNameError.value = e.response?.data?.error || 'Failed to update league name';
  } finally {
    updateLeagueNameLoading.value = false;
  }
};

const moveSelectedGames = async () => {
  if (!activeComparePlayerId.value || !selectedGameIdsForActive.value.size || !moveDialog.value.targetPlayerId) {
    moveError.value = 'Select games and target player ID.';
    return;
  }
  moveLoading.value = true;
  moveError.value = null;
  moveSuccess.value = null;
  try {
    await axios.post('/api/compare-players/move-games', {
      source_player_id: activeComparePlayerId.value,
      target_player_id: moveDialog.value.targetPlayerId,
      game_ids: Array.from(selectedGameIdsForActive.value)
    });
    moveSuccess.value = 'Games moved successfully.';
    // Clear selections for source
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [activeComparePlayerId.value]: [] };
    await fetchComparisonData();
  } catch (error) {
    moveError.value = 'Failed to move games';
    console.error('Error moving games:', error);
  } finally {
    moveLoading.value = false;
  }
};

const deleteSelectedGames = async () => {
  if (!activeComparePlayerId.value || !selectedGameIdsForActive.value.size) {
    deleteError.value = 'No games selected.';
    return;
  }
  
  deleteLoading.value = true;
  deleteError.value = null;
  
  try {
    const boxscoreIds = Array.from(selectedGameIdsForActive.value).map(id => parseInt(id));
    const playerId = activeComparePlayerId.value;
    
    // Use bulk delete endpoint
    await axios.post('/api/compare-players/delete-boxscores', {
      boxscore_ids: boxscoreIds
    });
    
    // Clear selections
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [playerId]: [] };
    
    // Refresh comparison data
    await fetchComparisonData();
    
    // Close dialog
    closeDeleteDialog();
  } catch (error) {
    deleteError.value = 'Failed to delete games: ' + (error.response?.data?.message || error.message);
    console.error('Error deleting games:', error);
  } finally {
    deleteLoading.value = false;
  }
};

const selectedGameIdsForDeletion = computed(() => {
  if (!selectedGameIdsForActive.value.size || !gamesForActivePlayer.value.length) {
    return [];
  }
  // Get the game identifier from selected boxscore records: use game_id when available, else rgm_game_id
  const selectedBoxscoreIds = Array.from(selectedGameIdsForActive.value);
  return gamesForActivePlayer.value
    .filter(game => selectedBoxscoreIds.includes(String(game.id)))
    .map(game => game.game_id ?? game.gameId ?? game.rgm_game_id)
    .filter(id => id !== null && id !== undefined && id !== '');
});

const uniqueGameIdsForDeletion = computed(() => {
  return [...new Set(selectedGameIdsForDeletion.value)];
});

const boxscoresToDeleteByGameId = computed(() => {
  if (!uniqueGameIdsForDeletion.value.length) return [];
  const allGames = comparisonResult.value?.data || [];
  const ids = uniqueGameIdsForDeletion.value;
  return allGames.filter(g => {
    const gameId = String(g.game_id ?? g.gameId ?? '');
    const rgmId = String(g.rgm_game_id ?? '');
    return ids.includes(gameId) || ids.includes(rgmId);
  });
});

const playersAffectedByGameIdDeletion = computed(() => {
  const boxscores = boxscoresToDeleteByGameId.value;
  const playerIds = new Set();
  boxscores.forEach(bs => {
    const pid = toIdString(bs.player_id || bs.playerId);
    if (pid) playerIds.add(pid);
  });
  return Array.from(playerIds);
});

const deleteGamesByGameId = async () => {
  if (!activeComparePlayerId.value || !uniqueGameIdsForDeletion.value.length) {
    deleteGameByIdError.value = 'No games selected.';
    return;
  }
  
  deleteGameByIdLoading.value = true;
  deleteGameByIdError.value = null;
  
  try {
    const gameIds = uniqueGameIdsForDeletion.value.map(id => String(id));
    
    // Use the dedicated endpoint that deletes all boxscores for these game_ids
    const response = await axios.post('/api/compare-players/delete-games-by-game-id', {
      game_ids: gameIds
    });
    
    // Clear selections
    const playerId = activeComparePlayerId.value;
    selectedGamesByPlayer.value = { ...selectedGamesByPlayer.value, [playerId]: [] };
    
    // Refresh comparison data
    await fetchComparisonData();
    
    // Close dialog
    closeDeleteGameByIdDialog();
  } catch (error) {
    deleteGameByIdError.value = 'Failed to delete games by game ID: ' + (error.response?.data?.message || error.message);
    console.error('Error deleting games by game ID:', error);
  } finally {
    deleteGameByIdLoading.value = false;
  }
};

const addOverlaySearchPlayer = (player, keepListOpen = false) => {
  const id = toIdString(player.player_id || player.id);
  if (!id) return;
  const name = player.player_name || player.name || `Player ${id}`;
  if (!props.selectedPlayers[id]) {
    const updated = { ...props.selectedPlayers, [id]: { id, name } };
    emit('players-updated', updated);
    // fetchComparisonData will be called automatically via watch on selectedPlayersList
  }
  if (!keepListOpen) {
    overlaySearch.value = '';
    overlaySearchResults.value = [];
  }
};

watch(overlaySearch, (val) => {
  if (!val || val.length < 2) {
    overlaySearchResults.value = [];
    return;
  }
  if (overlaySearchDebounce) clearTimeout(overlaySearchDebounce);
  overlaySearchDebounce = setTimeout(async () => {
    overlaySearchLoading.value = true;
    try {
      const isNumeric = /^\d+$/.test(val.trim());
      let response;
      if (isNumeric) {
        // Search by player_id
        response = await axios.get(`/api/players/search-players/${val}`, {
          params: { search_by: 'id' }
        });
      } else {
        // Search by name
        response = await axios.get(`/api/players/search-players/${val}`);
      }
      overlaySearchResults.value = response.data || [];
    } catch (e) {
      overlaySearchResults.value = [];
    } finally {
      overlaySearchLoading.value = false;
    }
  }, 300);
});

watch(activeComparePlayerId, (pid) => {
  if (pid) {
    fetchPlayerResources(pid);
  }
});

watch(selectedPlayersList, (list) => {
  if (!list.length) {
    activeComparePlayerId.value = null;
    closeOverlay();
    return;
  }
  if (!activeComparePlayerId.value || !list.find(p => p.id === activeComparePlayerId.value)) {
    activeComparePlayerId.value = list[0]?.id || null;
  }
  // Fetch stats when players list changes
  if (list.length && props.modelValue) {
    fetchComparisonData();
  }
}, { deep: true });

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    const list = selectedPlayersList.value;
    if (list.length) {
      if (!activeComparePlayerId.value) {
        activeComparePlayerId.value = list[0]?.id || null;
      }
      fetchComparisonData();
      if (activeComparePlayerId.value) {
        fetchPlayerResources(activeComparePlayerId.value);
      }
    }
  }
});

watch(() => props.initialTab, (newTab) => {
  if (newTab) {
    activePlayerTab.value = newTab;
  }
});

const fetchComparisonData = async () => {
  if (!selectedPlayerIds.value.length) return;
  comparisonLoading.value = true;
  comparisonError.value = null;
  selectedGamesByPlayer.value = {};
  moveError.value = null;
  moveSuccess.value = null;

  try {
    const response = await axios.get('/api/boxscores-management/comparable-data', {
      params: {
        player_ids: selectedPlayerIds.value.join(','),
        per_page: 100
      }
    });

    comparisonResult.value = response.data;
    if (!activeComparePlayerId.value) {
      activeComparePlayerId.value = toIdString(selectedPlayerIds.value[0] || null);
    }
    // Fetch player stats (projections / balanced lines / boxscores / rosters)
    try {
      const statsResp = await axios.get('/api/compare-players/stats', {
        params: { player_ids: selectedPlayerIds.value.join(',') }
      });
      // Normalize keys to strings for consistent lookup
      const normalized = {};
      Object.entries(statsResp.data || {}).forEach(([k, v]) => {
        normalized[toIdString(k)] = v;
      });
      playerStats.value = normalized;
    } catch (e) {
      console.error('Failed to fetch player stats', e);
    }
    if (activeComparePlayerId.value) {
      await fetchPlayerResources(activeComparePlayerId.value);
    }
  } catch (error) {
    comparisonError.value = 'Failed to load comparable games';
    comparisonResult.value = null;
    console.error('Error fetching comparable data comparison:', error);
  } finally {
    comparisonLoading.value = false;
  }
};

const handleOpenCompareOverlay = (event) => {
  const tab = event?.detail?.tab;
  if (tab) {
    activePlayerTab.value = tab;
  }
  emit('update:modelValue', true);
  if (activeComparePlayerId.value) {
    fetchPlayerResources(activeComparePlayerId.value);
  }
};

const closeTableContextMenu = () => {
  tableContextMenu.value.visible = false;
  document.removeEventListener('click', closeTableContextMenu);
};

const onTableContextMenu = (event) => {
  if (!selectedGameIdsForActive.value.size) return;
  event.preventDefault();
  tableContextMenu.value = { visible: true, x: event.clientX, y: event.clientY };
  document.addEventListener('click', closeTableContextMenu);
};

onMounted(() => {
  window.addEventListener('open-compare-overlay', handleOpenCompareOverlay);
  if (props.modelValue && selectedPlayersList.value.length) {
    fetchComparisonData();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('open-compare-overlay', handleOpenCompareOverlay);
  document.removeEventListener('click', closeSelectedContextMenu);
  document.removeEventListener('click', closeTableContextMenu);
  if (overlaySearchDebounce) clearTimeout(overlaySearchDebounce);
});
</script>

<template>
  <!-- Comparison Overlay -->
  <div 
    v-if="modelValue" 
    class="fixed inset-0 bg-black/60 z-[9998] flex items-center justify-center p-4"
    data-no-global-context-menu
  >
    <div class="absolute inset-0" @click="closeOverlay"></div>
    <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] h-[90vh] flex flex-col">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <i class="fas fa-exchange-alt text-pink-500"></i>
          <div>
            <div class="text-base font-semibold text-gray-800">Compare selected players</div>
            <div class="text-xs text-gray-500">{{ selectedPlayerIds.length }} selected</div>
          </div>
        </div>
        <div class="flex items-center space-x-3">
          <div v-if="comparisonLoading" class="text-sm text-gray-500">
            <i class="fas fa-spinner fa-spin mr-1"></i> Loading...
          </div>
          <button 
            class="text-gray-500 hover:text-gray-700" 
            title="Clear selection"
            @click="clearSelection"
          >
            Clear
          </button>
          <button 
            class="p-2 rounded-full hover:bg-gray-100 text-gray-500" 
            @click="closeOverlay"
            title="Close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <div class="flex flex-1 min-h-0 divide-x divide-gray-200">
        <!-- Left: selected players -->
        <div class="min-w-[400px] p-4 overflow-auto">
          <div class="flex items-center justify-between mb-3">
            <div class="text-sm font-semibold text-gray-700">Selected players</div>
            <div class="text-xs text-gray-500">{{ selectedPlayersList.length }}</div>
          </div>
          <div class="mb-3 space-y-2">
            <div class="text-xs text-gray-600 font-semibold">Add player</div>
            <input
              v-model="overlaySearch"
              type="text"
              class="w-full px-2 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
              placeholder="Search by name or player ID..."
            />
            <div v-if="overlaySearchLoading" class="text-xs text-gray-500">Searching...</div>
            <div v-else-if="overlaySearchResults.length" class="border pt-8 rounded max-h-[20rem] overflow-auto divide-y divide-gray-100 relative">
              <button 
                class="absolute top-1 right-1 translate-y-1/2 text-xs text-gray-500 hover:text-gray-700 px-2"
                @click="overlaySearchResults = []; overlaySearch = ''"
              >
                ✕
              </button>
              <div 
                v-for="p in overlaySearchResults" 
                :key="p.player_id || p.id"
                class="w-full text-left px-3 py-2 text-sm hover:bg-pink-50 flex justify-between items-center cursor-pointer"
                :class="{
                  'bg-pink-50 text-pink-700': !!selectedPlayers[valueOrId(p)]
                }"
                @click="addOverlaySearchPlayer(p)"
              >
                <div class="flex flex-col">
                  <span>{{ p.player_name || p.name }}</span>
                  <span class="text-gray-500 text-xs flex items-center gap-2">
                    <span>ID: {{ p.player_id || p.id }}</span>
                    <span v-if="selectedPlayers[valueOrId(p)]" class="text-pink-600 font-semibold text-[11px]">Added</span>
                  </span>
                </div>
                <button 
                  class="text-xs px-2 py-1 rounded border border-pink-200 text-pink-600 hover:bg-pink-50"
                  @click.stop="addOverlaySearchPlayer(p, true)"
                >
                  Add
                </button>
              </div>
            </div>

          </div>
          <div v-if="!selectedPlayersList.length" class="text-sm text-gray-500">
            No players selected.
          </div>
          <ul class="space-y-2">
            <li 
              v-for="player in selectedPlayersList" 
              :key="player.id" 
              class="flex items-center justify-between px-3 py-2 rounded border border-gray-200 hover:border-pink-200 hover:bg-pink-50 cursor-pointer"
              :class="{ 'border-pink-400 bg-pink-50': player.id === activeComparePlayerId }"
              @click="setActiveComparePlayer(player.id)"
              @contextmenu.prevent="openSelectedContextMenu($event, player)"
            >
              <div>
                <div 
                  class="text-sm font-semibold text-gray-800"
                  contenteditable="true"
                  spellcheck="false"
                  @click.stop
                  @blur="onPlayerNameBlur(player, $event)"
                  @keydown.enter.prevent="onPlayerNameBlur(player, $event); $event.target.blur();"
                >{{ player.name }}</div>
                <div class="text-xs text-gray-500">ID: {{ player.id }}</div>
              </div>
              <div class="flex items-center gap-2">
                <div class="flex items-center gap-2 text-[11px] text-gray-600">
                  <span>P: {{ getPlayerStat(player.id, 'projections') }}</span>
                  <span>BL: {{ getPlayerStat(player.id, 'balanced_lines') }}</span>
                  <span>BS: {{ getPlayerStat(player.id, 'boxscores') }}</span>
                  <span 
                    class="relative"
                    @mouseenter="rosterHoverPlayer = player.id"
                    @mouseleave="rosterHoverPlayer = null"
                  >
                    R: {{ getPlayerStat(player.id, 'rosters_count') }}
                    <div 
                      v-if="rosterHoverPlayer === player.id && (playerStats[toIdString(player.id)]?.rosters?.length)" 
                      class="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg rounded px-3 py-2 text-[11px] text-gray-700 z-10 min-w-[160px]"
                    >
                      <div class="font-semibold mb-1">Rosters</div>
                      <div v-for="r in playerStats[toIdString(player.id)]?.rosters || []" :key="r.team_id" class="py-0.5">
                        {{ r.team_name || ('Team ' + r.team_id) }}
                      </div>
                    </div>
                  </span>
                  <span>A: {{ getPlayerStat(player.id, 'aliases') }}</span>
                </div>
                <button 
                  class="text-gray-400 hover:text-gray-600" 
                  @click.stop="removeFromSelectedOnly(player)"
                  title="Remove from selected list"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </li>
          </ul>
        </div>

        <!-- Right: comparison tabs + detail tabs -->
        <div class="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
          <div class="px-4 pt-4 flex flex-wrap gap-2">
            <button
              v-for="player in selectedPlayersList"
              :key="player.id"
              class="px-3 py-2 text-sm rounded border transition-colors"
              :class="player.id === activeComparePlayerId 
                ? 'bg-pink-500 text-white border-pink-500' 
                : 'bg-white text-gray-700 border-gray-200 hover:border-pink-200'"
              @click="setActiveComparePlayer(player.id)"
            >
              {{ player.name || 'Player' }} ({{ player.id }})
            </button>
          </div>

          <div class="px-4 pt-2 flex items-center gap-2 text-sm">
            <button 
              class="px-3 py-1 rounded border text-sm"
              :class="activePlayerTab === 'boxscores' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-700 border-gray-200'"
              @click="activePlayerTab = 'boxscores'"
            >Boxscores ({{ tabCount('boxscores') }})</button>
            <button 
              class="px-3 py-1 rounded border text-sm"
              :class="activePlayerTab === 'rosters' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-700 border-gray-200'"
              @click="activePlayerTab = 'rosters'; fetchPlayerResources(activeComparePlayerId)"
            >Rosters ({{ tabCount('rosters') }})</button>
            <button 
              class="px-3 py-1 rounded border text-sm"
              :class="activePlayerTab === 'projections' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-700 border-gray-200'"
              @click="activePlayerTab = 'projections'; fetchPlayerResources(activeComparePlayerId)"
            >Projections ({{ tabCount('projections') }})</button>
            <button 
              class="px-3 py-1 rounded border text-sm"
              :class="activePlayerTab === 'balanced' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-700 border-gray-200'"
              @click="activePlayerTab = 'balanced'; fetchPlayerResources(activeComparePlayerId)"
            >Balanced Lines ({{ tabCount('balanced') }})</button>
            <button 
              class="px-3 py-1 rounded border text-sm"
              :class="activePlayerTab === 'aliases' ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-gray-700 border-gray-200'"
              @click="activePlayerTab = 'aliases'; fetchPlayerResources(activeComparePlayerId)"
            >Aliases ({{ tabCount('aliases') }})</button>
            <span v-if="resourcesLoading" class="text-xs text-gray-500"><i class="fas fa-spinner fa-spin mr-1"></i>Loading</span>
            <span v-if="resourcesError" class="text-xs text-red-500">{{ resourcesError }}</span>
          </div>

          <div class="flex-1 min-h-0 min-w-0 overflow-auto overflow-y-scroll-visible px-4 pb-4" @contextmenu.prevent="onTableContextMenu" data-no-global-context-menu>
            <div v-if="comparisonError" class="text-sm text-red-500 mt-3">{{ comparisonError }}</div>
            <div v-else-if="comparisonLoading" class="text-sm text-gray-500 mt-3">
              <i class="fas fa-spinner fa-spin mr-2"></i> Loading comparable games...
            </div>

            <template v-else>
              <!-- Boxscores tab -->
              <div v-if="activePlayerTab === 'boxscores'">
                <div v-if="!gamesForActivePlayer.length" class="text-sm text-gray-500 mt-3">
                  No games found for this player.
                </div>
                <div v-else class="mt-4">
                  <div class="flex items-center justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <label class="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input type="checkbox" class="form-checkbox text-pink-500" :checked="isAllGamesSelectedForActive" @change="toggleSelectAllForActive" />
                        Select all
                      </label>
                    </div>
                    <div class="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      {{ gamesForActivePlayer.length }} games found
                      <span v-if="selectedGameIdsForActive.size" class="text-xs text-pink-600">({{ selectedGameIdsForActive.size }} selected)</span>
                    </div>
                  </div>
                  <div class="w-full overflow-x-auto overflow-y-auto overflow-y-scroll-visible rounded border border-gray-200" style="max-height: calc(90vh - 300px);">
                    <table class="min-w-full text-xs whitespace-nowrap">
                      <thead class="bg-gray-100 text-gray-700 uppercase">
                        <tr>
                          <th class="px-3 py-2 text-left w-8"></th>
                          <th class="px-3 py-2 text-left">#</th>
                          <th class="px-3 py-2 text-left">Date</th>
                          <th class="px-3 py-2 text-left">Game ID</th>
                          <th class="px-3 py-2 text-left">RGM game ID</th>

                          <th class="px-3 py-2 text-left">League</th>
                          <th class="px-3 py-2 text-left">Season</th>

                          <th class="px-3 py-2 text-left">Player ID</th>
                          <th class="px-3 py-2 text-left">PB ID</th>
                          <th class="px-3 py-2 text-left">Player Name</th>
                          <th class="px-3 py-2 text-left">Team</th>
                          <th class="px-3 py-2 text-left">Opponent</th>
                          <th class="px-3 py-2 text-right">Min</th>
                          <th class="px-3 py-2 text-right">PTS</th>
                          <th class="px-3 py-2 text-right">REB</th>
                          <th class="px-3 py-2 text-right">AST</th>
                          <th class="px-3 py-2 text-right">3PM</th>
                          <th class="px-3 py-2 text-right">STL</th>
                          <th class="px-3 py-2 text-right">BLK</th>
                          <th class="px-3 py-2 text-right">TO</th>
                          <th class="px-3 py-2 text-right">PRA</th>
                          <th class="px-3 py-2 text-right">EFF</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-100 text-gray-800">
                        <tr v-for="(game, idx) in gamesForActivePlayer" :key="game.id || idx" class="hover:bg-pink-50">
                          <td class="px-3 py-2">
                            <input type="checkbox" class="form-checkbox text-pink-500" :checked="isGameSelected(game)" @change="toggleGameSelection(game)" />
                          </td>
                          <td class="px-3 py-2">{{ idx + 1 }}</td>
                          <td class="px-3 py-2 whitespace-nowrap">{{ game.game_date }}</td>
                          <td class="px-3 py-2">{{ game.game_id || '—' }}</td>
                          <td class="px-3 py-2">{{ game.rgm_game_id || '—' }}</td>
                          <td class="px-3 py-2">
                            <button
                              v-if="game.league_name"
                              class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                              @click="openLeagueGamesDialog(game.league_name, game.season)"
                              :title="`Show ${game.league_name} games`"
                            >
                              {{ game.league_name }}
                            </button>
                            <span v-else>—</span>
                          </td>
                          <td class="px-3 py-2">
                            <button
                              v-if="game.season"
                              class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                              @click="openLeagueGamesDialog(game.league_name, game.season)"
                              :title="`Show ${game.league_name} ${game.season} games`"
                            >
                              {{ game.season }}
                            </button>
                            <span v-else>—</span>
                          </td>
                          <td class="px-3 py-2">{{ game.player_id || '—' }}</td>
                          <td class="px-3 py-2">{{ game.p_player_id || '—' }}</td>
                          <td class="px-3 py-2">{{ game.player_name || '—' }}</td>
                          <td class="px-3 py-2">
                            <button
                              v-if="game.team_name"
                              class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                              @click="openFilteredGamesDialog('team', game.team_name, game.season)"
                              :title="`Show ${game.team_name} games`"
                            >
                              {{ game.team_name }}
                            </button>
                            <span v-else>—</span>
                          </td>
                          <td class="px-3 py-2">
                            <button
                              v-if="game.opponent_team_name || game.opponent_team"
                              class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                              @click="openFilteredGamesDialog('opponent', game.opponent_team_name || game.opponent_team, game.season)"
                              :title="`Show games vs ${game.opponent_team_name || game.opponent_team}`"
                            >
                              {{ game.opponent_team_name || game.opponent_team }}
                            </button>
                            <span v-else>—</span>
                          </td>
                          <td class="px-3 py-2 text-right">{{ game.minutes ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.points ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.total_rebounds ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.assists ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.three_points_made ?? game["3pm"] ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.steals ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.blocks ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.turnovers ?? '—' }}</td>
                          <td class="px-3 py-2 text-right">{{ game.pra ?? (game.points !== undefined && game.assists !== undefined && game.total_rebounds !== undefined ? game.points + game.assists + game.total_rebounds : '—') }}</td>
                          <td class="px-3 py-2 text-right">{{ game.efficiency ?? game.eff ?? '—' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- Rosters tab -->
              <div v-else-if="activePlayerTab === 'rosters'">
                <div v-if="!(playerResources[activeComparePlayerId]?.rosters?.length)" class="text-sm text-gray-500 mt-3">
                  No rosters found.
                </div>
                <div v-else class="mt-3 space-y-2">
                  <div 
                    v-for="r in playerResources[activeComparePlayerId].rosters" 
                    :key="r.id || r.team_id" 
                    class="p-2 border border-gray-200 rounded text-sm bg-gray-50"
                  >
                    <div class="font-semibold text-gray-800">{{ r.team_name || ('Team ' + r.team_id) }}</div>
                    <div class="text-xs text-gray-500">Team ID: {{ r.team_id }}</div>
                    <div class="text-xs text-gray-500">Added: {{ r.created_at || '—' }}</div>
                  </div>
                </div>
              </div>

              <!-- Projections tab -->
              <div v-else-if="activePlayerTab === 'projections'">
                <div v-if="!(playerResources[activeComparePlayerId]?.projections?.length)" class="text-sm text-gray-500 mt-3">
                  No projections found.
                </div>
                <div v-else class="mt-3 overflow-x-auto overflow-y-auto rounded border border-gray-200">
                  <table class="min-w-full text-xs">
                    <thead class="bg-gray-100 text-gray-700 uppercase">
                      <tr>
                        <th class="px-3 py-2 text-left">ID</th>
                        <th class="px-3 py-2 text-left">Player ID</th>
                        <th class="px-3 py-2 text-left">Fixture</th>
                        <th class="px-3 py-2 text-left">League</th>
                        <th class="px-3 py-2 text-left">Home Team</th>
                        <th class="px-3 py-2 text-left">Away Team</th>
                        <th class="px-3 py-2 text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-gray-800">
                      <tr v-for="p in playerResources[activeComparePlayerId].projections" :key="p.id">
                        <td class="px-3 py-2">{{ p.id }}</td>
                        <td class="px-3 py-2">{{ p.player_id || '—' }}</td>
                        <td class="px-3 py-2">{{ p.fixture_id || '—' }}</td>
                        <td class="px-3 py-2">{{ p.league_name || '—' }}</td>
                        <td class="px-3 py-2">{{ p.home_team_name || '—' }}</td>
                        <td class="px-3 py-2">{{ p.away_team_name || '—' }}</td>
                        <td class="px-3 py-2">{{ p.created_at || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Balanced lines tab -->
              <div v-else-if="activePlayerTab === 'balanced'">
                <div v-if="!(playerResources[activeComparePlayerId]?.balanced_lines?.length)" class="text-sm text-gray-500 mt-3">
                  No balanced lines found.
                </div>
                <div v-else class="mt-3 overflow-x-auto overflow-y-auto rounded border border-gray-200">
                  <table class="min-w-full text-xs">
                    <thead class="bg-gray-100 text-gray-700 uppercase">
                      <tr>
                        <th class="px-3 py-2 text-left">ID</th>
                        <th class="px-3 py-2 text-left">Fixture</th>
                        <th class="px-3 py-2 text-left">Team type</th>
                        <th class="px-3 py-2 text-left">Minutes</th>
                        <th class="px-3 py-2 text-left">Pts/30</th>
                        <th class="px-3 py-2 text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-gray-800">
                      <tr v-for="b in playerResources[activeComparePlayerId].balanced_lines" :key="b.id">
                        <td class="px-3 py-2">{{ b.id }}</td>
                        <td class="px-3 py-2">{{ b.fixture_id || '—' }}</td>
                        <td class="px-3 py-2">{{ b.team_type || '—' }}</td>
                        <td class="px-3 py-2">{{ b.projected_minutes ?? '—' }}</td>
                        <td class="px-3 py-2">{{ b.points_per_30 ?? '—' }}</td>
                        <td class="px-3 py-2">{{ b.created_at || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Aliases tab -->
              <div v-else-if="activePlayerTab === 'aliases'">
                <div v-if="!(playerResources[activeComparePlayerId]?.aliases?.length)" class="text-sm text-gray-500 mt-3">
                  No aliases found.
                </div>
                <div v-else class="mt-3 overflow-x-auto overflow-y-auto rounded border border-gray-200">
                  <table class="min-w-full text-xs">
                    <thead class="bg-gray-100 text-gray-700 uppercase">
                      <tr>
                        <th class="px-3 py-2 text-left">ID</th>
                        <th class="px-3 py-2 text-left">Player ID</th>
                        <th class="px-3 py-2 text-left">Alias Name</th>
                        <th class="px-3 py-2 text-left">Created</th>
                        <th class="px-3 py-2 text-left">Updated</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 text-gray-800">
                      <tr v-for="a in playerResources[activeComparePlayerId].aliases" :key="a.id">
                        <td class="px-3 py-2">{{ a.id }}</td>
                        <td class="px-3 py-2">{{ a.player_id || '—' }}</td>
                        <td class="px-3 py-2">{{ a.player_name || '—' }}</td>
                        <td class="px-3 py-2">{{ a.created_at || '—' }}</td>
                        <td class="px-3 py-2">{{ a.updated_at || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Table context menu -->
    <div 
      v-if="tableContextMenu.visible" 
      class="fixed z-[9999] bg-white shadow-lg rounded-md border border-gray-200 w-56 py-2"
      :style="{ top: `${tableContextMenu.y}px`, left: `${tableContextMenu.x}px` }"
      @click.stop
      data-no-global-context-menu
    >
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-pink-50 flex items-center justify-between disabled:opacity-50"
        :disabled="!selectedGameIdsForActive.size"
        @click="openMoveDialog"
      >
        <span>Move selected games</span>
        <span class="text-xs text-gray-500">{{ selectedGameIdsForActive.size }}</span>
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center justify-between disabled:opacity-50"
        :disabled="!selectedGameIdsForActive.size"
        @click="openDeleteDialog"
      >
        <span>Delete selected games</span>
        <span class="text-xs text-gray-500">{{ selectedGameIdsForActive.size }}</span>
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-red-100 text-red-700 font-semibold flex items-center justify-between disabled:opacity-50"
        :disabled="!selectedGameIdsForActive.size || !uniqueGameIdsForDeletion.length"
        @click="openDeleteGameByIdDialog"
      >
        <span>Delete game(s) by game ID</span>
        <span class="text-xs text-gray-500">{{ uniqueGameIdsForDeletion.length }}</span>
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 flex items-center justify-between disabled:opacity-50"
        :disabled="!selectedGameIdsForActive.size || !uniqueGameIdsForDeletion.length"
        @click="openFullGameDialog"
      >
        <span>Show full game</span>
        <span class="text-xs text-gray-500">{{ uniqueGameIdsForDeletion.length }} game(s)</span>
      </button>
    </div>

    <!-- Move dialog -->
    <div 
      v-if="moveDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="moveDialog.open = false"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-base font-semibold text-gray-800">Move selected games</div>
            <div class="text-xs text-gray-500">From player {{ activeComparePlayerId }}</div>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="moveDialog.open = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="text-sm text-gray-700">
          Selected games: {{ selectedGameIdsForActive.size }}
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">Target player ID</label>
          <input 
            v-model="moveDialog.targetPlayerId"
            type="text"
            class="w-full px-3 py-2 border rounded focus:outline-none focus:border-pink-500"
            placeholder="Enter target player id"
          />
          <div v-if="moveCandidatePlayers.length" class="mt-2">
            <label class="block text-xs text-gray-600 mb-1">Pick from selected players</label>
            <select 
              class="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-pink-500"
              :value="moveDialog.targetPlayerId"
              @change="moveDialog.targetPlayerId = $event.target.value"
            >
              <option value="" disabled>Select player</option>
              <option 
                v-for="p in moveCandidatePlayers" 
                :key="p.id" 
                :value="p.id"
              >
                {{ p.name }} ({{ p.id }})
              </option>
            </select>
          </div>
        </div>
        <div v-if="moveError" class="text-xs text-red-500">{{ moveError }}</div>
        <div v-if="moveSuccess" class="text-xs text-green-600">{{ moveSuccess }}</div>
        <div class="flex justify-end gap-3">
          <button class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800" @click="moveDialog.open = false">Cancel</button>
          <button 
            class="px-3 py-2 text-sm text-white bg-pink-500 hover:bg-pink-600 rounded disabled:opacity-50"
            :disabled="moveLoading"
            @click="moveSelectedGames"
          >
            <i v-if="moveLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Confirm move
          </button>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <div 
      v-if="deleteDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeDeleteDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-base font-semibold text-gray-800">Delete selected games</div>
            <div class="text-xs text-gray-500">Player ID: {{ activeComparePlayerId }}</div>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="closeDeleteDialog" :disabled="deleteLoading">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="text-sm text-gray-700">
          <p class="mb-2">Are you sure you want to delete <strong>{{ selectedGameIdsForActive.size }}</strong> selected game(s)?</p>
          <p class="text-xs text-gray-500 mb-2">This action cannot be undone.</p>
          <div v-if="selectedGameIdsForActive.size > 0 && selectedGameIdsForActive.size <= 10" class="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-2">
            <div class="font-semibold mb-1">Boxscore IDs to delete:</div>
            <div class="flex flex-wrap gap-1">
              <span v-for="gameId in Array.from(selectedGameIdsForActive).slice(0, 10)" :key="gameId" class="px-2 py-0.5 bg-white border border-gray-200 rounded">
                {{ gameId }}
              </span>
            </div>
          </div>
        </div>
        <div v-if="deleteError" class="text-xs text-red-500 bg-red-50 p-2 rounded">{{ deleteError }}</div>
        <div class="flex justify-end gap-3">
          <button 
            class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800" 
            @click="closeDeleteDialog"
            :disabled="deleteLoading"
          >
            Cancel
          </button>
          <button 
            class="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded disabled:opacity-50"
            :disabled="deleteLoading"
            @click="deleteSelectedGames"
          >
            <i v-if="deleteLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Delete {{ selectedGameIdsForActive.size }} game(s)
          </button>
        </div>
      </div>
    </div>

    <!-- Delete game by game ID dialog -->
    <div 
      v-if="deleteGameByIdDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeDeleteGameByIdDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-lg p-6 space-y-4">
          <div class="flex items-center justify-between">
          <div>
            <div class="text-base font-semibold text-red-700">Delete game(s) by game ID</div>
            <div class="text-xs text-gray-500">Player ID: {{ activeComparePlayerId }}</div>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="closeDeleteGameByIdDialog" :disabled="deleteGameByIdLoading">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="text-sm text-gray-700">
          <div class="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
            <div class="flex items-start gap-2">
              <i class="fas fa-exclamation-triangle text-yellow-600 mt-0.5"></i>
              <div>
                <p class="font-semibold text-yellow-800 mb-1">Warning: This will delete the FULL game</p>
                <p class="text-xs text-yellow-700 mb-1">This action will delete <strong>all boxscore records</strong> for the selected game ID(s) that are in the current view. This means <strong>all players</strong> visible in the comparison who have boxscores for these games will have their records deleted.</p>
                <p class="text-xs text-yellow-800 font-semibold">Note: This will delete boxscores for all players who have records for these game IDs in the current dataset.</p>
              </div>
            </div>
          </div>
          <p class="mb-2">Are you sure you want to delete all boxscores for <strong>{{ uniqueGameIdsForDeletion.length }}</strong> game ID(s)?</p>
          <p class="text-xs text-red-600 font-semibold mb-2">This action cannot be undone.</p>
          <div v-if="uniqueGameIdsForDeletion.length > 0" class="text-xs text-gray-600 bg-gray-50 p-2 rounded mt-2 space-y-2">
            <div>
              <div class="font-semibold mb-1">Game ID(s) to delete:</div>
              <div class="flex flex-wrap gap-1">
                <span v-for="gameId in uniqueGameIdsForDeletion" :key="gameId" class="px-2 py-0.5 bg-white border border-gray-200 rounded">
                  {{ gameId }}
                </span>
              </div>
            </div>
            <div v-if="boxscoresToDeleteByGameId.length > 0">
              <div class="font-semibold mb-1">Boxscores to delete: {{ boxscoresToDeleteByGameId.length }}</div>
              <div v-if="playersAffectedByGameIdDeletion.length > 0" class="mt-1">
                <div class="font-semibold mb-1">Affected player(s): {{ playersAffectedByGameIdDeletion.length }}</div>
                <div class="flex flex-wrap gap-1">
                  <span v-for="pid in playersAffectedByGameIdDeletion.slice(0, 5)" :key="pid" class="px-2 py-0.5 bg-white border border-gray-200 rounded">
                    {{ pid }}
                  </span>
                  <span v-if="playersAffectedByGameIdDeletion.length > 5" class="px-2 py-0.5 bg-white border border-gray-200 rounded text-gray-500">
                    +{{ playersAffectedByGameIdDeletion.length - 5 }} more
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="deleteGameByIdError" class="text-xs text-red-500 bg-red-50 p-2 rounded">{{ deleteGameByIdError }}</div>
        <div class="flex justify-end gap-3">
          <button 
            class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800" 
            @click="closeDeleteGameByIdDialog"
            :disabled="deleteGameByIdLoading"
          >
            Cancel
          </button>
          <button 
            class="px-3 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
            :disabled="deleteGameByIdLoading"
            @click="deleteGamesByGameId"
          >
            <i v-if="deleteGameByIdLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Delete {{ boxscoresToDeleteByGameId.length }} boxscore(s) for {{ uniqueGameIdsForDeletion.length }} game ID(s)
          </button>
        </div>
      </div>
    </div>

    <!-- Show full game dialog -->
    <div 
      v-if="fullGameDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeFullGameDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div class="text-base font-semibold text-blue-700">Full game — all players</div>
            <div class="text-xs text-gray-500">Game ID(s): {{ uniqueGameIdsForDeletion.join(', ') }}</div>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="closeFullGameDialog">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div v-if="fullGameLoading" class="p-8 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
          <div>Loading all players for this game...</div>
        </div>
        <div v-else-if="fullGameError" class="p-4 text-sm text-red-600 bg-red-50 rounded mx-4 mt-2">{{ fullGameError }}</div>
        <div v-else class="flex-1 overflow-auto overflow-y-scroll-visible p-4">
          <div v-if="!fullGameBoxscores.length" class="text-sm text-gray-500">No boxscores found for these game ID(s).</div>
          <div v-else>
            <div class="flex items-center justify-between gap-4 mb-3">
              <label class="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" class="form-checkbox text-blue-600" :checked="fullGameIsAllSelected" @change="toggleFullGameSelectAll" />
                Select all
              </label>
              <div class="flex items-center gap-2">
                <span v-if="fullGameSelectedCount" class="text-xs text-gray-600">{{ fullGameSelectedCount }} selected</span>
                <button
                  class="px-3 py-1.5 text-sm rounded border bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="!fullGameSelectedCount || fullGameDeleteLoading"
                  @click="deleteSelectedFullGameBoxscores"
                >
                  <i v-if="fullGameDeleteLoading" class="fas fa-spinner fa-spin mr-1"></i>
                  Delete selected
                </button>
              </div>
            </div>
            <div v-if="fullGameDeleteError" class="text-xs text-red-600 mb-2">{{ fullGameDeleteError }}</div>
            <div class="overflow-x-auto">
              <table class="min-w-full text-xs">
                <thead class="bg-gray-100 text-gray-700 uppercase">
                  <tr>
                    <th class="px-3 py-2 text-left w-8">
                      <input type="checkbox" class="form-checkbox text-blue-600" :checked="fullGameIsAllSelected" @change="toggleFullGameSelectAll" />
                    </th>
                    <th class="px-3 py-2 text-left">#</th>
                    <th class="px-3 py-2 text-left">Date</th>
                    <th class="px-3 py-2 text-left">Game ID</th>
                    <th class="px-3 py-2 text-left">RGM ID</th>
                    <th class="px-3 py-2 text-left">League</th>
                    <th class="px-3 py-2 text-left">Player ID</th>
                    <th class="px-3 py-2 text-left">Player Name</th>
                    <th class="px-3 py-2 text-left">Team</th>
                    <th class="px-3 py-2 text-left">Opponent</th>
                    <th class="px-3 py-2 text-right">Min</th>
                    <th class="px-3 py-2 text-right">PTS</th>
                    <th class="px-3 py-2 text-right">REB</th>
                    <th class="px-3 py-2 text-right">AST</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 text-gray-800">
                  <tr v-for="(b, idx) in fullGameBoxscores" :key="b.id || idx" class="hover:bg-blue-50" :class="{ 'bg-blue-100': isFullGameRowSelected(b) }">
                    <td class="px-3 py-2">
                      <input type="checkbox" class="form-checkbox text-blue-600" :checked="isFullGameRowSelected(b)" @change="toggleFullGameRow(b)" />
                    </td>
                    <td class="px-3 py-2">{{ idx + 1 }}</td>
                    <td class="px-3 py-2">{{ b.game_date || '—' }}</td>
                    <td class="px-3 py-2">{{ b.game_id ?? '—' }}</td>
                    <td class="px-3 py-2">{{ b.rgm_game_id ?? '—' }}</td>
                    <td class="px-3 py-2">{{ b.league_name || '—' }}</td>
                    <td class="px-3 py-2">{{ b.player_id ?? '—' }}</td>
                    <td class="px-3 py-2">{{ b.player_name || '—' }}</td>
                    <td class="px-3 py-2">
                      <button
                        v-if="b.team_name"
                        class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                        @click="closeFullGameDialog(); openFilteredGamesDialog('team', b.team_name, b.season)"
                        :title="`Show ${b.team_name} games`"
                      >
                        {{ b.team_name }}
                      </button>
                      <span v-else>—</span>
                    </td>
                    <td class="px-3 py-2">
                      <button
                        v-if="b.opponent_team_name || b.opponent_team"
                        class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                        @click="closeFullGameDialog(); openFilteredGamesDialog('opponent', b.opponent_team_name || b.opponent_team, b.season)"
                        :title="`Show games vs ${b.opponent_team_name || b.opponent_team}`"
                      >
                        {{ b.opponent_team_name || b.opponent_team }}
                      </button>
                      <span v-else>—</span>
                    </td>
                    <td class="px-3 py-2 text-right">{{ b.minutes ?? '—' }}</td>
                    <td class="px-3 py-2 text-right">{{ b.points ?? '—' }}</td>
                    <td class="px-3 py-2 text-right">{{ b.total_rebounds ?? '—' }}</td>
                    <td class="px-3 py-2 text-right">{{ b.assists ?? '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="text-xs text-gray-500 mt-2">{{ fullGameBoxscores.length }} player(s) in this game</div>
          </div>
        </div>
      </div>
    </div>

    <!-- League games dialog -->
    <div 
      v-if="leagueGamesDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeLeagueGamesDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-[95vw] max-h-[90vh] flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-wrap gap-2">
          <div>
            <div class="text-base font-semibold text-blue-700">
              {{ leagueGamesFilterType === 'league' ? 'League' : leagueGamesFilterType === 'team' ? 'Team' : 'Opponent' }} games — {{ leagueGamesFilterLabel }}{{ leagueGamesSeason ? ' · ' + leagueGamesSeason : '' }}
            </div>
            <div class="text-xs text-gray-500">Ordered by game date (latest first)</div>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="leagueGamesFilterType === 'league' && leagueGamesFilterLabel"
              class="px-2 py-1 text-xs bg-amber-100 text-amber-800 hover:bg-amber-200 rounded"
              @click="openUpdateLeagueNameDialog"
            >
              Update League Name
            </button>
            <label class="text-xs text-gray-600">Limit:</label>
            <input
              v-model.number="leagueGamesPerPage"
              type="number"
              min="1"
              max="10000"
              class="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:border-blue-500"
            />
            <button
              class="px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded disabled:opacity-50"
              :disabled="leagueGamesLoading"
              @click="fetchLeagueGames"
            >
              Reload
            </button>
            <button class="text-gray-500 hover:text-gray-700" @click="closeLeagueGamesDialog">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div v-if="leagueGamesLoading" class="p-8 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin text-2xl mb-2"></i>
          <div>Loading games...</div>
        </div>
        <div v-else-if="leagueGamesError" class="p-4 text-sm text-red-600 bg-red-50 rounded mx-4 mt-2">{{ leagueGamesError }}</div>
        <div v-else class="flex-1 overflow-auto overflow-y-scroll-visible p-4">
          <div v-if="!leagueGamesData.length" class="text-sm text-gray-500">No games found.</div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full text-xs">
              <thead class="bg-gray-100 text-gray-700 uppercase">
                <tr>
                  <th class="px-3 py-2 text-left">#</th>
                  <th class="px-3 py-2 text-left">Date</th>
                  <th class="px-3 py-2 text-left">Game ID</th>
                  <th class="px-3 py-2 text-left">RGM ID</th>
                  <th class="px-3 py-2 text-left">League</th>
                  <th class="px-3 py-2 text-left">Player</th>
                  <th class="px-3 py-2 text-left">Team</th>
                  <th class="px-3 py-2 text-left">Opponent</th>
                  <th class="px-3 py-2 text-right">Min</th>
                  <th class="px-3 py-2 text-right">PTS</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 text-gray-800">
                <tr v-for="(g, idx) in leagueGamesData" :key="g.id || idx" class="hover:bg-blue-50">
                  <td class="px-3 py-2">{{ idx + 1 }}</td>
                  <td class="px-3 py-2">{{ g.game_date || '—' }}</td>
                  <td class="px-3 py-2">{{ g.game_id ?? '—' }}</td>
                  <td class="px-3 py-2">{{ g.rgm_game_id ?? '—' }}</td>
                  <td class="px-3 py-2">
                    <button
                      v-if="g.league_name"
                      class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                      @click="openLeagueGamesDialog(g.league_name, g.season)"
                      :title="`Show ${g.league_name} games`"
                    >
                      {{ g.league_name }}
                    </button>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-2">{{ g.player_name || '—' }} ({{ g.player_id ?? '—' }})</td>
                  <td class="px-3 py-2">
                    <button
                      v-if="g.team_name"
                      class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                      @click="openFilteredGamesDialog('team', g.team_name, g.season)"
                      :title="`Show ${g.team_name} games`"
                    >
                      {{ g.team_name }}
                    </button>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-2">
                    <button
                      v-if="g.opponent_team_name || g.opponent_team"
                      class="text-blue-600 hover:text-blue-800 underline cursor-pointer text-left"
                      @click="openFilteredGamesDialog('opponent', g.opponent_team_name || g.opponent_team, g.season)"
                      :title="`Show games vs ${g.opponent_team_name || g.opponent_team}`"
                    >
                      {{ g.opponent_team_name || g.opponent_team }}
                    </button>
                    <span v-else>—</span>
                  </td>
                  <td class="px-3 py-2 text-right">{{ g.minutes ?? '—' }}</td>
                  <td class="px-3 py-2 text-right">{{ g.points ?? '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="text-xs text-gray-500 mt-2">{{ leagueGamesData.length }} record(s)</div>
        </div>
      </div>
    </div>

    <!-- Update League Name dialog -->
    <div
      v-if="updateLeagueNameDialog.open"
      class="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeUpdateLeagueNameDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div class="text-base font-semibold text-gray-800">Update League Name</div>
        <div class="space-y-3">
          <div>
            <label class="block text-xs text-gray-600 mb-1">Current league name</label>
            <div class="px-3 py-2 bg-gray-100 rounded text-sm">{{ leagueGamesFilterLabel }}</div>
          </div>
          <div v-if="updateLeagueNameRowCount !== null" class="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded">
            <strong>{{ updateLeagueNameRowCount }}</strong> record(s) will be updated
          </div>
          <div>
            <label class="block text-xs text-gray-600 mb-1">New league name</label>
            <input
              v-model="updateLeagueNameNewName"
              type="text"
              class="w-full px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
              placeholder="Enter new league name"
            />
          </div>
        </div>
        <div v-if="updateLeagueNameError" class="text-xs text-red-500">{{ updateLeagueNameError }}</div>
        <div class="flex justify-end gap-2">
          <button class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800" @click="closeUpdateLeagueNameDialog">
            Cancel
          </button>
          <button
            class="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
            :disabled="updateLeagueNameLoading || !updateLeagueNameNewName?.trim() || updateLeagueNameNewName?.trim() === leagueGamesFilterLabel"
            @click="submitUpdateLeagueName"
          >
            <i v-if="updateLeagueNameLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Update
          </button>
        </div>
      </div>
    </div>

    <!-- Selected player context menu -->
    <div 
      v-if="selectedContextMenu.visible" 
      class="fixed z-[9999] bg-white shadow-lg rounded-md border border-gray-200 w-56 py-2"
      :style="{ top: `${selectedContextMenu.y}px`, left: `${selectedContextMenu.x}px` }"
      @click.stop
      data-no-global-context-menu
    >
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-pink-50"
        @click="removeFromSelectedOnly(selectedContextMenu.player); closeSelectedContextMenu()"
      >
        Remove from selected list
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600"
        @click="openDeletePlayerDialog(selectedContextMenu.player)"
      >
        Delete player from database
      </button>
    </div>

    <!-- Delete player dialog -->
    <div 
      v-if="deletePlayerDialog.open" 
      class="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
      data-no-global-context-menu
    >
      <div class="absolute inset-0" @click="closeDeletePlayerDialog"></div>
      <div class="relative z-10 bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div class="flex items-center justify-between">
          <div>
            <div class="text-base font-semibold text-red-700">Delete player from database</div>
            <div class="text-xs text-gray-500">Player ID: {{ deletePlayerDialog.player?.id }}</div>
          </div>
          <button class="text-gray-500 hover:text-gray-700" @click="closeDeletePlayerDialog" :disabled="deletePlayerLoading">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="text-sm text-gray-700">
          <div class="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <div class="flex items-start gap-2">
              <i class="fas fa-exclamation-triangle text-red-600 mt-0.5"></i>
              <div>
                <p class="font-semibold text-red-800 mb-1">Warning: This will permanently delete the player</p>
                <p class="text-xs text-red-700">This action will delete the player record from the <strong>players</strong> table. This action cannot be undone.</p>
              </div>
            </div>
          </div>
          <p class="mb-2">Are you sure you want to delete player <strong>{{ deletePlayerDialog.player?.name }}</strong> (ID: {{ deletePlayerDialog.player?.id }})?</p>
          <p class="text-xs text-red-600 font-semibold mb-2">This action cannot be undone.</p>
        </div>
        <div v-if="deletePlayerError" class="text-xs text-red-500 bg-red-50 p-2 rounded">{{ deletePlayerError }}</div>
        <div class="flex justify-end gap-3">
          <button 
            class="px-3 py-2 text-sm text-gray-600 hover:text-gray-800" 
            @click="closeDeletePlayerDialog"
            :disabled="deletePlayerLoading"
          >
            Cancel
          </button>
          <button 
            class="px-3 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
            :disabled="deletePlayerLoading"
            @click="deletePlayerFromTable"
          >
            <i v-if="deletePlayerLoading" class="fas fa-spinner fa-spin mr-1"></i>
            Delete player
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Force visible scrollbars on the right */
.overflow-y-scroll-visible {
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}
.overflow-y-scroll-visible::-webkit-scrollbar {
  width: 10px;
}
.overflow-y-scroll-visible::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.overflow-y-scroll-visible::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}
.overflow-y-scroll-visible::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}
</style>
